import { addAuthzMetadataGrpcCall, buildGrpcClient } from "../../../../../lib/grpc";
import { GlobalActionsConfigRepository } from "../../domain/interfaces/GlobalActionsConfigRepository";
import { GlobalActionsConfigEntity } from "../../domain/globalActionConfig.entity";
import { GlobalActionsConfigMapper } from "../mappers/GlobalActionsConfigMapper";
import { GlobalActionsAPIPromiseClient } from "../../../../../gen/witbooking/backoffice/orchestrator/globalactions/v1/global_actions_api_grpc_web_pb";
import {
  CreateGlobalActionsConfigRequest,
  DeleteGlobalActionsConfigRequest,
  GetAllGlobalActionsConfigsRequest,
  GetGlobalActionsConfigRequest,
  ReorderGlobalActionsConfigRequest,
  UpdateGlobalActionsConfigRequest,
} from "../../../../../gen/witbooking/backoffice/orchestrator/globalactions/v1/global_actions_api_pb";

export class GlobalActionsConfigGrpcRepository implements GlobalActionsConfigRepository {
  private readonly client: GlobalActionsAPIPromiseClient;

  constructor() {
    this.client = buildGrpcClient(GlobalActionsAPIPromiseClient);
  }

  async findByProperty(propertyTicker: string): Promise<GlobalActionsConfigEntity[]> {
    const request = new GetAllGlobalActionsConfigsRequest();
    request.setPropertyTicker(propertyTicker);

    const call = addAuthzMetadataGrpcCall(this.client, this.client.getAllGlobalActionsConfigs);
    const response = await call(request, { "x-property": propertyTicker });

    return GlobalActionsConfigMapper.toDomainArray(response.getConfigsList());
  }

  async findById(propertyTicker: string, id: string): Promise<GlobalActionsConfigEntity | null> {
    const request = new GetGlobalActionsConfigRequest();
    request.setPropertyTicker(propertyTicker);
    request.setId(id);

    const call = addAuthzMetadataGrpcCall(this.client, this.client.getGlobalActionsConfig);
    const response = await call(request, { "x-property": propertyTicker });

    const config = response.getConfig();
    return config ? GlobalActionsConfigMapper.toDomain(config) : null;
  }

  async create(
    propertyTicker: string,
    entity: GlobalActionsConfigEntity
  ): Promise<GlobalActionsConfigEntity> {
    const request = new CreateGlobalActionsConfigRequest();
    request.setPropertyTicker(propertyTicker);
    request.setConfig(GlobalActionsConfigMapper.toProto(entity));

    const call = addAuthzMetadataGrpcCall(this.client, this.client.createGlobalActionsConfig);
    const response = await call(request, { "x-property": propertyTicker });

    const config = response.getConfig();
    if (!config) {
      throw new Error("Unexpected response creating GlobalActionsConfig");
    }
    return GlobalActionsConfigMapper.toDomain(config);
  }

  async update(propertyTicker: string, entity: GlobalActionsConfigEntity): Promise<void> {
    const request = new UpdateGlobalActionsConfigRequest();
    request.setPropertyTicker(propertyTicker);
    request.setConfig(GlobalActionsConfigMapper.toProto(entity));

    const call = addAuthzMetadataGrpcCall(this.client, this.client.updateGlobalActionsConfig);
    await call(request, { "x-property": propertyTicker });
  }

  async delete(propertyTicker: string, id: string): Promise<void> {
    const request = new DeleteGlobalActionsConfigRequest();
    request.setPropertyTicker(propertyTicker);
    request.setId(id);

    const call = addAuthzMetadataGrpcCall(this.client, this.client.deleteGlobalActionsConfig);
    await call(request, { "x-property": propertyTicker });
  }

  async reorder(propertyTicker: string, id: string, after: string): Promise<void> {
    const request = new ReorderGlobalActionsConfigRequest();
    request.setPropertyTicker(propertyTicker);
    request.setId(id);
    request.setAfter(after);

    const call = addAuthzMetadataGrpcCall(this.client, this.client.reorderGlobalActionsConfig);
    await call(request, { "x-property": propertyTicker });
  }
}
